import { useRef, useState } from 'react'
import { t } from '@/lib/i18n'

/*
  HoldButton — the design-bundle "hold-to-confirm" action pattern.

  States: idle → holding (progress fills 0→100% over `holdMs`) → loading
  (spinner) → success (check + label, 1.2s) → idle. A rejected action goes to
  `error` and STAYS there (red label) until the user holds again to retry —
  honest state over a silent reset, since replay is consequential and the user
  must know whether it actually went out.

  No toasts: the button itself carries every state. The hold gesture is the
  design's intentional friction on a one-tap action that could mis-fire.
  Releasing before the hold completes cancels and snaps back to idle.
*/
export function HoldButton({
  onAction,
  children,
  holdMs = 420,
  className,
  style,
  variant = 'primary',
  disabled = false,
  errorLabel = t('failed'),
}: {
  onAction: () => Promise<unknown> | unknown
  children: React.ReactNode
  holdMs?: number
  className?: string
  style?: React.CSSProperties
  variant?: 'primary' | 'secondary'
  disabled?: boolean
  errorLabel?: React.ReactNode
}) {
  type State = 'idle' | 'holding' | 'loading' | 'success' | 'error'
  const [state, setState] = useState<State>('idle')
  const [progress, setProgress] = useState(0)
  const rafRef = useRef<number | null>(null)
  const cancelRef = useRef(false)

  const cancelHold = () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
    cancelRef.current = true
    setProgress(0)
    if (state === 'holding') setState('idle')
  }

  const onDown = () => {
    // Re-holding from a failed attempt retries; loading/success are locked.
    if ((state !== 'idle' && state !== 'error') || disabled) return
    cancelRef.current = false
    setState('holding')
    const start = Date.now()
    const tick = () => {
      if (cancelRef.current) return
      const p = Math.min(1, (Date.now() - start) / holdMs)
      setProgress(p)
      if (p >= 1) {
        // Threshold hit → run the action
        setProgress(0)
        setState('loading')
        Promise.resolve(onAction())
          .then(() => {
            setState('success')
            setTimeout(() => setState('idle'), 1200)
          })
          .catch(() => setState('error')) // persist until the user re-holds
        return
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
  }

  const isPrimary = variant === 'primary'
  const baseColor = isPrimary ? 'var(--accent)' : 'var(--surface)'
  const textColor = isPrimary ? 'var(--on-accent)' : 'var(--text)'

  const label =
    state === 'loading'
      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Spinner />{t('replaying')}</span>
      : state === 'success'
        ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><CheckIcon />{t('done')}</span>
        : state === 'error'
          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><AlertIcon />{errorLabel}</span>
          : children

  const background = state === 'success'
    ? 'var(--green)'
    : state === 'error'
      ? 'color-mix(in srgb, var(--red) 12%, transparent)'
      : baseColor
  const color = state === 'success'
    ? 'var(--on-accent)'
    : state === 'error'
      ? 'var(--red)'
      : textColor
  const border = state === 'error'
    ? '1px solid color-mix(in srgb, var(--red) 40%, transparent)'
    : isPrimary ? 'none' : '1px solid var(--border)'

  return (
    <button
      type="button"
      onMouseDown={onDown}
      onMouseUp={cancelHold}
      onMouseLeave={cancelHold}
      onTouchStart={onDown}
      onTouchEnd={cancelHold}
      onTouchCancel={cancelHold}
      disabled={disabled || state === 'loading'}
      className={`tap ${className ?? ''}`}
      aria-live="polite"
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: '0.5rem 1rem',
        borderRadius: '0.5rem',
        border,
        background,
        color,
        fontWeight: 500,
        fontSize: '0.875rem',
        cursor: disabled || state === 'loading' ? 'default' : 'pointer',
        overflow: 'hidden',
        userSelect: 'none',
        touchAction: 'none',
        transition: 'background 160ms ease, color 160ms ease',
        ...style,
      }}
    >
      {state === 'holding' && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: `${progress * 100}%`,
            background: 'rgba(255,255,255,0.25)',
            transition: 'none',
          }}
        />
      )}
      <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {label}
      </span>
    </button>
  )
}

function Spinner() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" style={{ animation: 'whl-spin 700ms linear infinite' }}>
      <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.4" fill="none" opacity="0.25" />
      <path d="M 6 1.5 A 4.5 4.5 0 0 1 10.5 6" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 6.5 L5 9 L 9.5 3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="14"
        strokeDashoffset="14"
        style={{ animation: 'whl-check 400ms ease-out forwards' }}
      />
    </svg>
  )
}

function AlertIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M6 1.6 L11 10.4 H1 Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M6 5 V7.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="6" cy="8.9" r="0.55" fill="currentColor" />
    </svg>
  )
}
